import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchJson, postJson, putJson } from '../lib/api';

// --- Types ---

export interface PlaybookStep {
  id: string;
  title: string;
  description?: string;
  assigneeRole?: string;
  estimatedMinutes?: number;
  required: boolean;
  order: number;
}

export interface Playbook {
  id: string;
  name: string;
  description?: string;
  category: string;
  steps: PlaybookStep[];
  estimatedDuration?: number;
  createdAt: string;
  updatedAt: string;
}

export interface PlaybookInstanceStep {
  stepId: string;
  status: 'pending' | 'completed' | 'skipped';
  completedAt?: string;
  completedBy?: string;
  notes?: string;
}

export interface PlaybookInstance {
  id: string;
  playbookId: string;
  playbookName: string;
  entityType?: string;
  entityId?: string;
  entityName?: string;
  status: 'active' | 'completed' | 'cancelled';
  steps: PlaybookInstanceStep[];
  startedAt: string;
  completedAt?: string;
}

export interface PlaybooksResponse {
  playbooks: Playbook[];
  total: number;
}

export interface PlaybookInstancesResponse {
  instances: PlaybookInstance[];
  total: number;
}

// --- Hooks ---

export function usePlaybooks() {
  return useQuery({
    queryKey: ['playbooks'],
    queryFn: () => fetchJson<PlaybooksResponse>('/api/playbooks'),
  });
}

export function usePlaybook(id: string | null) {
  return useQuery({
    queryKey: ['playbook', id],
    queryFn: () => fetchJson<Playbook>(`/api/playbooks/${id}`),
    enabled: !!id,
  });
}

export function useCreatePlaybook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Playbook>) => postJson<Playbook>('/api/playbooks', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbooks'] });
    },
  });
}

export function useStartPlaybook() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: { playbookId: string; entityType?: string; entityId?: string }) =>
      postJson<PlaybookInstance>('/api/playbooks/start', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-instances'] });
    },
  });
}

export function usePlaybookInstances() {
  return useQuery({
    queryKey: ['playbook-instances'],
    queryFn: () => fetchJson<PlaybookInstancesResponse>('/api/playbooks/instances'),
  });
}

export function useUpdatePlaybookStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      instanceId,
      stepId,
      data,
    }: {
      instanceId: string;
      stepId: string;
      data: { status: 'completed' | 'skipped'; notes?: string };
    }) => putJson<PlaybookInstance>(`/api/playbooks/instances/${instanceId}/steps/${stepId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playbook-instances'] });
    },
  });
}
